"use client"

import { useEffect, type ReactNode } from "react"
import { AuthBridgeService, HostType } from "@gorilla-works/bridge"

/**
 * Wires workflow-hub up as an FBO host on the @gorilla-works/bridge so that
 * sync-mfe (mounted alongside or inside this app via Module Federation) can
 * signal "navigate back to the FBO host" by publishing to `fboRoute$`.
 *
 * The publisher side lives in sync-mfe — e.g. the back arrow in its request
 * detail / pre-onboard pages calls
 * `authBridgeService.navigateToFboRoute(FboRoutes.WorkflowHub)`, which the
 * bridge normalizes to a `/next/`-prefixed path and emits on `fboRoute$`.
 *
 * Without a subscriber, those emissions are dropped and the user gets stuck
 * inside `/next/mfe/sync/...` (sync-mfe's internal fallback) instead of
 * returning to `/next/app/workflow-hub-v2`.
 *
 * Why `window.location.assign` and not Next's router?
 *   - The emitted route is rooted at the Angular shell (`/next/...`) rather
 *     than inside this Next.js basePath. A Next router push would treat the
 *     value as an internal path and produce a broken URL.
 *   - The destination may be a separate microapp mounted by the shell; a
 *     hard navigation is what flips the shell back to the workflow-hub
 *     microapp. The shell mirror of this subscription
 *     (`appNext/src/app/mfe-bridge/components/container-mfe`) does the same
 *     thing via Angular's router; we use `location.assign` because we don't
 *     own the shell's routing here.
 *
 * The bridge singleton is window-scoped via `getOrCreate`, so this hooks
 * into whichever instance sync-mfe is publishing on as long as they share
 * the same window (which they do under Module Federation).
 */
export function HostBridgeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (typeof window === "undefined") return

    const authBridgeService = AuthBridgeService.getOrCreate(HostType.FBO)

    const sub = authBridgeService.fboRoute$.subscribe((route) => {
      if (!route) return
      // Route arrives already `/next/`-prefixed (see auth-bridge.service
      // normalization in @gorilla-works/bridge). No-op when we're already
      // at the target — avoids a pointless reload if a stale emission fires
      // after we've landed on the workflow-hub URL.
      if (
        typeof window !== "undefined"
        && window.location.pathname !== route
      ) {
        window.location.assign(route)
      }
    })

    return () => sub.unsubscribe()
  }, [])

  return <>{children}</>
}
